import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  FaPencilAlt,
  FaPaperPlane,
  FaSpinner,
  FaCheckCircle,
  FaInfoCircle,
  FaSave,
} from "react-icons/fa";
import { useAuth } from "../../context/AuthContext";
import PageHeader from "../../core/common/header/PageHeader";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

type WritingTask = {
  id: number;
  level: string;
  taskDescription: string;
};

const StudentWritingTaskPage: React.FC = () => {
  const { taskId } = useParams<{ taskId: string }>();
  const { student } = useAuth();
  const navigate = useNavigate();

  const [task, setTask] = useState<WritingTask | null>(null);
  const [writing, setWriting] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [submission, setSubmission] = useState<any>(null);
  const [showSaveWarning, setShowSaveWarning] = useState(false);

  // ✅ CRITICAL: Add refs for synchronous submission tracking
  const submittingRef = useRef(false);
  const hasSubmittedRef = useRef(false);

  useEffect(() => {
    fetchTask();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId]);

  const fetchTask = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/student/writing-tasks/${taskId}`,
        { credentials: "include" }
      );

      if (response.ok) {
        const data = await response.json();
        setTask(data);
      } else {
        setError("Failed to load task details");
      }
    } catch {
      setError("Network error");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // ✅ CRITICAL: Check refs first (synchronous check)
    if (submittingRef.current || hasSubmittedRef.current) {
      console.log("Submission already in progress or completed");
      return;
    }

    // ✅ Set refs immediately (before any async operations)
    submittingRef.current = true;
    setSubmitting(true);
    setError("");

    if (!writing.trim()) {
      setError("Please write something to submit");
      submittingRef.current = false;
      setSubmitting(false);
      return;
    }

    if (writing.trim().length < 20) {
      setError("Writing must be at least 20 characters long");
      submittingRef.current = false;
      setSubmitting(false);
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/student/writing-tasks/${taskId}/submit?studentId=${student?.studentId}`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ writing: writing.trim() }),
        }
      );

      const data = await response.json();

      if (response.ok) {
        // ✅ Mark as submitted permanently
        hasSubmittedRef.current = true;

        setSubmission(data);
        setWriting("");
        setShowSaveWarning(true);
      } else {
        // ✅ Check if it's a duplicate submission error
        if (
          data.error?.includes("already submitted") ||
          data.error?.toLowerCase().includes("duplicate")
        ) {
          // Mark as submitted permanently
          hasSubmittedRef.current = true;
          setError(data.error);

          // Optionally navigate away or show message
          setTimeout(() => {
            navigate("/student/my-writing-tasks");
          }, 2000);
        } else {
          // Reset only for other errors
          setError(data.error || "Failed to submit writing task");
          submittingRef.current = false;
          setSubmitting(false);
        }
      }
    } catch (err) {
      console.error("Error submitting writing task:", err);
      setError("Network error. Please try again.");
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const handleCopyFeedback = () => {
    if (submission?.aiFeedback) {
      const textToCopy = `Score: ${submission.score}/10\n\n${submission.aiFeedback}\n\nYour Writing:\n${submission.studentWriting}`;
      navigator.clipboard
        .writeText(textToCopy)
        .then(() => {
          alert("Feedback copied to clipboard! ফিডব্যাক কপি হয়ে গেছে!");
        })
        .catch(() => {
          alert("Failed to copy. Please copy manually.");
        });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <FaSpinner className="animate-spin text-[#009fe3] text-4xl" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <PageHeader
        icon={<FaPencilAlt />}
        title="Writing Task"
        breadcrumb={[
          { label: "Home", to: "/" },
          { label: "Dashboard", to: "/student/student-dashboard" },
          { label: "Writing Tasks", to: "/student/my-writing-tasks" },
          { label: "Complete Task" },
        ]}
      />

      {/* Content */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {/* Bengali Instructions Section */}
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-400 rounded-xl p-6 mb-6 shadow-lg">
          <div className="flex items-start gap-4">
            <FaInfoCircle className="text-green-600 text-3xl flex-shrink-0 mt-1" />
            <div className="flex-1">
              <h3 className="text-xl font-bold text-green-900 mb-3">
                কীভাবে Writing Task সম্পন্ন করবেন
              </h3>
              <div className="space-y-3 text-sm text-green-800 leading-relaxed">
                <p>
                  <strong>১.</strong> প্রথমে প্রশ্নটি মনোযোগ দিয়ে পড়ুন এবং{" "}
                  <strong className="text-green-900">
                    শুধুমাত্র জার্মান ভাষায়
                  </strong>{" "}
                  প্রশ্ন অনুযায়ী উত্তর লিখুন।
                </p>
                <p>
                  <strong>২.</strong> আপনার লেখা সম্পন্ন হলে{" "}
                  <strong className="text-green-900">Submit</strong> বাটনে ক্লিক
                  করুন। মনে রাখবেন, সাবমিট করার পর আপনার উত্তরের মূল্যায়ন
                  (evaluation) হতে{" "}
                  <strong className="text-green-900">
                    একটু সময় লাগতে পারে
                  </strong>
                  , তাই দয়া করে ধৈর্য ধরবেন।
                </p>
                <p>
                  <strong>৩.</strong> সিস্টেম মূল্যায়ন শেষ হলে এটি আপনাকে{" "}
                  <strong className="text-green-900">
                    আপনার করা ভুলগুলো দেখাবে
                  </strong>{" "}
                  এবং একই সঙ্গে আপনার লেখার একটি{" "}
                  <strong className="text-green-900">
                    অপ্টিমাইজড (উন্নত) সংস্করণ
                  </strong>
                  ও প্রদান করবে।
                </p>
                <p>
                  <strong>৪.</strong> চাইলে আপনি{" "}
                  <strong className="text-green-900">
                    স্ক্রিনশট নিয়ে রাখতে পারেন
                  </strong>
                  , অথবা ভবিষ্যতে পড়ার জন্য কোথাও{" "}
                  <strong className="text-green-900">
                    কপি করে রাখতে পারেন
                  </strong>
                  ।
                </p>
                <p className="text-base font-bold text-green-900 mt-4">
                  ✅ আপনি এখনই আপনার Writing Task করতে পারেন।
                </p>
                <p className="text-base font-bold text-red-900 mt-2">
                  ⚠️ একবার সাবমিট করার পর আর পুনরায় সাবমিট করতে পারবেন না!
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Task Instructions */}
        {task && (
          <div className="bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200 rounded-xl p-6 mb-6 shadow-sm">
            <h2 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
              <FaInfoCircle className="text-blue-600" />
              Task Instructions
            </h2>
            <div className="space-y-2">
              <div>
                <span className="font-semibold text-gray-700">Level: </span>
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
                  {task.level}
                </span>
              </div>
              <div className="mt-3">
                <p className="text-gray-800 leading-relaxed whitespace-pre-wrap">
                  {task.taskDescription}
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Input Section */}
          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-200">
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <FaPencilAlt className="text-[#009fe3]" />
              Your Writing
            </h2>

            {!submission ? (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Write in German (জার্মান ভাষায় লিখুন)
                  </label>
                  <textarea
                    value={writing}
                    onChange={(e) => setWriting(e.target.value)}
                    disabled={submitting || hasSubmittedRef.current}
                    placeholder="এখানে আপনার German text লিখুন... (Write your German text here...)"
                    rows={14}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent transition-all text-sm resize-none disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ fontSize: "16px" }}
                    required
                  />
                  <div className="text-xs text-gray-500 mt-1">
                    {writing.length} characters (minimum 20 characters required)
                  </div>
                </div>

                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                    {error}
                  </div>
                )}

                <div className="flex gap-3">
                  <button
                    type="submit"
                    disabled={submitting || hasSubmittedRef.current}
                    className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-[#009fe3] to-cyan-500 text-white rounded-lg font-semibold hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                  >
                    {submitting || submittingRef.current ? (
                      <>
                        <FaSpinner className="animate-spin" />
                        Submitting & Evaluating...
                      </>
                    ) : (
                      <>
                        <FaPaperPlane />
                        Submit
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => setWriting("")}
                    disabled={submitting || hasSubmittedRef.current}
                    className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 disabled:opacity-50 transition-colors"
                  >
                    Clear
                  </button>
                </div>
              </form>
            ) : (
              <div className="space-y-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <FaCheckCircle className="text-green-600" />
                    <h3 className="font-semibold text-green-800">
                      Submitted Successfully!
                    </h3>
                  </div>
                  <p className="text-sm text-green-700">
                    Your writing has been evaluated. See the feedback on the
                    right.
                  </p>
                  <div className="mt-3 text-lg font-bold text-green-800">
                    Your Score: {submission.score}/10
                  </div>
                </div>

                {showSaveWarning && (
                  <div className="bg-amber-50 border-2 border-amber-400 rounded-lg p-4 animate-pulse">
                    <div className="flex items-start gap-2">
                      <FaSave className="text-amber-600 mt-0.5 text-xl flex-shrink-0" />
                      <div className="text-sm text-amber-800">
                        <p className="font-bold mb-1">
                          ⚠️ ফিডব্যাক সংরক্ষণ করুন!
                        </p>
                        <p>পরে আর দেখতে পারবেন না। এখনই কপি/স্ক্রিনশট করুন!</p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-700 mb-2">
                    Your Submission:
                  </h4>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">
                    {submission.studentWriting}
                  </p>
                </div>

                <button
                  onClick={handleCopyFeedback}
                  className="w-full px-6 py-3 bg-amber-500 text-white rounded-lg font-semibold hover:bg-amber-600 transition-colors flex items-center justify-center gap-2"
                >
                  <FaSave />
                  Copy Feedback (ফিডব্যাক কপি করুন)
                </button>

                <button
                  onClick={() => navigate("/student/my-writing-tasks")}
                  className="w-full px-6 py-3 bg-[#009fe3] text-white rounded-lg font-semibold hover:bg-cyan-600 transition-colors"
                >
                  Back to Writing Tasks
                </button>
              </div>
            )}
          </div>

          {/* Feedback Section */}
          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-200">
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <FaCheckCircle className="text-green-600" />
              Feedback & Corrections
            </h2>

            {submission ? (
              <>
                <div className="prose prose-sm max-w-none">
                  <div
                    className="text-sm text-gray-800 leading-relaxed"
                    style={{
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                    }}
                  >
                    {submission.aiFeedback
                      .split("\n")
                      .map((line: string, index: number) => {
                        if (line.includes("**")) {
                          const parts = line.split(/(\*\*.*?\*\*)/g);
                          return (
                            <p key={index} className="mb-2">
                              {parts.map((part: string, i: number) => {
                                if (
                                  part.startsWith("**") &&
                                  part.endsWith("**")
                                ) {
                                  return (
                                    <strong key={i} className="text-gray-900">
                                      {part.slice(2, -2)}
                                    </strong>
                                  );
                                }
                                return <span key={i}>{part}</span>;
                              })}
                            </p>
                          );
                        }
                        if (line.trim()) {
                          return (
                            <p key={index} className="mb-2">
                              {line}
                            </p>
                          );
                        }
                        return <br key={index} />;
                      })}
                  </div>
                </div>
              </>
            ) : (
              <div className="flex flex-col items-center justify-center h-64 text-gray-400">
                <FaPencilAlt className="text-5xl mb-4" />
                <p className="text-center text-sm">
                  Submit your writing to see AI feedback and corrections
                </p>
                <p className="text-center text-xs mt-2">
                  আপনার লেখা submit করুন feedback দেখতে
                </p>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default StudentWritingTaskPage;
